import React from 'react';
import styled from 'styled-components';

import MuiNativeSelect from '@mui/material/NativeSelect';
import InputBase from '@mui/material/InputBase';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { SxProps, Theme } from '@mui/material/styles';

import theme from 'shared/themes/default';
import type { BackgroundThemes } from 'shared/const';

import { InputStyle, ButtonStyle } from './styles';

const Icon = styled(ExpandMoreIcon)`
  right: ${theme.spacing.s};
`;

interface NativeSelectItem {
  key: string | number;
  value: string | number | boolean;
  label: string;
  disabled?: boolean;
}

interface NativeSelectProps {
  disabled?: boolean;
  hasAction?: boolean;
  hasBackground?: boolean;
  id: string;
  items?: NativeSelectItem[];
  name: string;
  onChange: (target: {
    name: string;
    value: string | number | boolean | (string | number | boolean)[];
  }) => void;
  required?: boolean;
  sx?: SxProps<Theme>;
  theme?: BackgroundThemes;
  value?: string | number | boolean | (string | number | boolean)[];
}

const NativeSelect = ({
  hasAction,
  hasBackground,
  id,
  items = [],
  name,
  onChange,
  required,
  sx: sxProp = {},
  theme: themeLayout,
  value,
  ...props
}: NativeSelectProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(event.target);
  };

  const textColor =
    themeLayout === 'dark' ? theme.palette.lightPure : theme.palette.mainDark;
  const disabledColor =
    themeLayout === 'dark'
      ? theme.palette.disabledDark
      : theme.palette.disabledLight;
  let colorIcon: string = hasBackground ? theme.palette.mainDark : textColor;
  colorIcon = hasAction ? colorIcon : theme.palette.darkDistinct;
  colorIcon = props.disabled ? disabledColor : colorIcon;

  const sx: SxProps<Theme> = {
    width: '100%',
    '& .MuiNativeSelect-iconStandard': {
      color: colorIcon,
    },
    ...sxProp,
  };

  const sxInputStyle = hasAction
    ? ButtonStyle(hasBackground, themeLayout)
    : InputStyle(themeLayout);

  return (
    <MuiNativeSelect
      variant="standard"
      input={<InputBase />}
      inputProps={{
        id,
        name,
      }}
      sx={[sx, sxInputStyle] as SxProps<Theme>}
      IconComponent={Icon}
      value={value}
      onChange={handleChange}
      required={required}
      {...props}
    >
      {items.map((item) => (
        <option
          value={String(item.value)}
          key={item.key}
          disabled={item.disabled}
        >
          {item.label}
        </option>
      ))}
    </MuiNativeSelect>
  );
};

export default NativeSelect;
export type { NativeSelectProps, NativeSelectItem };
